import dayjs from 'dayjs';
import {
  ClientFormWithCustomFields,
  ClientInputFormWithCustomFields,
  ParticipantInputFormWithCustomFields,
} from '@/features/public/booking/schemas/clientSchema.ts';
import { FillerType } from '@/features/public/booking/types/FillerType.ts';

/** Trzy pola rezerwującego przepisywane do wiersza uczestnika nr 0. */
export interface FillerIdentity {
  firstName?: string;
  lastName?: string;
  birthDate?: Date;
}

const toDateOrUndefined = (
  value: Date | string | undefined,
): Date | undefined => (value ? dayjs(value).toDate() : undefined);

const isSameDate = (a?: Date, b?: Date): boolean => {
  if (!a || !b) return a === b || (!a && !b);
  return a.getTime() === b.getTime();
};

/**
 * Wartości startowe formularza rezerwacji.
 *
 * Niezmiennik: zawsze co najmniej jeden wiersz uczestnika. Pusty formularz
 * otwierał się bez ani jednej karty, więc klient nie widział, gdzie właściwie
 * ma wpisać uczestnika — a szkic z przerwanej sesji potrafi wrócić z pustą
 * listą (np. gdy zapisał się przed dodaniem pierwszego uczestnika).
 */
export function getDefaultBookingFormData(
  cachedFormData: ClientFormWithCustomFields | undefined,
  getParticipantInitValues: () => ParticipantInputFormWithCustomFields,
): Partial<ClientInputFormWithCustomFields> {
  if (!cachedFormData) {
    return {
      fillerType: FillerType.CLIENT,
      participants: [getParticipantInitValues()],
    };
  }

  const participants = (cachedFormData.participants ?? []).map(
    (participant) => ({
      ...participant,
      // Szkic wraca z localStorage (persist zustanda), więc daty są tam stringami.
      birthDate: toDateOrUndefined(participant.birthDate),
    }),
  ) as ParticipantInputFormWithCustomFields[];

  return {
    ...cachedFormData,
    birthDate: toDateOrUndefined(cachedFormData.birthDate),
    participants:
      participants.length > 0 ? participants : [getParticipantInitValues()],
  } as Partial<ClientInputFormWithCustomFields>;
}

/**
 * Wiersz uczestnika nr 0 po przełączeniu „Formularz wypełnia".
 *
 * Przełącznik nie dodaje ani nie usuwa uczestników — uczestnik nr 0 po prostu
 * *staje się* rezerwującym albo przestaje nim być. Reszta wiersza (pola
 * dodatkowe, ubezpieczenie) zostaje nietknięta; wybory zależne od wieku —
 * opcje i transport wyceniają się po progach wiekowych — znikają dopiero wtedy,
 * gdy faktycznie zmienia się data urodzenia, czyli gdy wiersz opisuje już kogoś
 * innego.
 */
export function applyFillerTypeToFirstParticipant(
  participant: ParticipantInputFormWithCustomFields,
  fillerType: FillerType,
  client: FillerIdentity,
): ParticipantInputFormWithCustomFields {
  const identity: FillerIdentity =
    fillerType === FillerType.PARTICIPANT
      ? {
          firstName: client.firstName ?? '',
          lastName: client.lastName ?? '',
          birthDate: client.birthDate,
        }
      : { firstName: '', lastName: '', birthDate: undefined };

  const keepsAgeSensitiveChoices = isSameDate(
    participant.birthDate,
    identity.birthDate,
  );

  return {
    ...participant,
    ...identity,
    ...(keepsAgeSensitiveChoices
      ? {}
      : { selectedOptions: {}, transportVariantId: undefined }),
  } as ParticipantInputFormWithCustomFields;
}
